import * as storage from '../utils/storage';
import Settings from '../domain/Settings';
import {getGigyaId} from '../utils/gigya';
import {getCxenseProperties} from '../utils/cxense';
import {hasPurposesConsent, hasUserProfilingConsent, onConsent} from './privacyService';
import performance from '../utils/performance';

const PROFILE_SERVICE_URL = 'https://profile-public-api.data.dpgmedia.cloud/dmoi',
	DMOI_STORAGE_KEY = 'advert-dmoi',
	DMOI_FETCH_TIMEOUT = 1000;

let userId: string;

export async function init(): Promise<void> {
	if (!Settings.getInstance().features.dmoi) {
		userId = storage.getFromCookie('authId');

		return;
	}

	userId = _getFromStorage();

	if (!userId) {
		userId = await _fetchDMOI();

		_cacheUserId();
	}
}

function _getIdentifiers() {
	return Object.fromEntries(Object.entries({
		'pw_id': storage.getFromCookie('authId'),
		'gigya_id': getGigyaId(),
		'cxense_id': getCxenseProperties()?.id
	}).filter(([, v]) => v));
}

async function _fetchDMOI(): Promise<string | null> {
	try {
		performance.mark('user - dmoi - start');

		const identifiers = _getIdentifiers();

		if (Object.keys(identifiers).length === 0) {
			return null;
		}

		const {
				apiKey,
				url = PROFILE_SERVICE_URL
			} = Settings.getInstance().dmoi,
			abortController = new AbortController(),
			abortTimeout = setTimeout(() => {
				abortController.abort('Timed out');
			}, DMOI_FETCH_TIMEOUT),
			response = await window.fetch(url, {
				'method': 'POST',
				'headers': {

					// @ts-ignore
					'x-api-key': apiKey
				},

				// @ts-ignore
				'body': JSON.stringify(identifiers),
				'signal': abortController.signal
			});

		clearTimeout(abortTimeout);

		return (await response.json()).dmoi;
	} catch (error) {
		console.error('[ADVERT] Could not fetch DMOI', error);

		return null;
	} finally {
		performance.mark('user - dmoi - end');
	}
}

function _cacheUserId() {
	onConsent(() => {
		if (hasPurposesConsent([1])) {
			storage.put(DMOI_STORAGE_KEY, JSON.stringify({
				'value': userId,
				'expiration': _getExpirationDate()
			}));
		}
	});
}

function _getExpirationDate(): string {
	const currDate = new Date();

	currDate.setDate(currDate.getDate() + 1);
	currDate.setUTCHours(5, 0, 0, 0);

	return currDate.toISOString();
}

function _getFromStorage(): string | null {
	const storageValue = storage.get(DMOI_STORAGE_KEY);

	if (!storageValue) {
		return null;
	}

	const {
		expiration,
		value
	} = JSON.parse(storageValue);

	if (new Date(expiration) < new Date()) {
		return null;
	}

	return value;
}

export function getUserId(): string {
	if (!hasUserProfilingConsent()) {
		return null;
	}

	return userId;
}

export function isUserLoggedIn(): boolean {
	return !!getGigyaId();
}
